import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useThemeMode } from '../contexts/ThemeContext';

// ─── Orb definitions ─────────────────────────────────────
// docY  = the scroll position where this orb is centered in the viewport
// x     = horizontal position (CSS %)
// size  = diameter in px
// drift = parallax multiplier (0 = moves with scroll, 0.5 = moves at half speed)
//         Higher drift → orb lingers longer as you scroll past → more depth feel
const ORBS = [
  // Far-left warm amber — bleeds in from left edge near Hero
  { color: 'rgba(99, 102, 241, 0.10)', size: 1100, x: '-8%',  docY: 300,  drift: 0.35 },
  // Far-right purple — bleeds in from right edge through CoreFeatures
  { color: 'rgba(168, 85, 247, 0.12)', size: 1000, x: '108%', docY: 1200, drift: 0.25 },
  // Far-left cyan — drifts through WorkflowAnimation / Partners
  { color: 'rgba(6, 182, 212, 0.11)',  size: 1050, x: '-5%',  docY: 2200, drift: 0.40 },
  // Far-right amber — visible in IndustryFocus / Technologies
  { color: 'rgba(99, 102, 241, 0.09)',  size: 1150, x: '105%', docY: 3400, drift: 0.30 },
  // Far-left violet — flows through DeploymentOptions
  { color: 'rgba(139, 92, 246, 0.11)', size: 950,  x: '-6%',  docY: 4400, drift: 0.45 },
  // Far-right teal — visible near CallToAction / Contact
  { color: 'rgba(14, 116, 144, 0.12)', size: 1080, x: '106%', docY: 5400, drift: 0.20 },
];

export default function LightOrbs() {
  const { isLight } = useThemeMode();
  const containerRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!isLight) return;

    const update = () => {
      if (!containerRef.current) return;
      const scrollY = window.scrollY;
      const vh = window.innerHeight;
      const children = containerRef.current.children;

      for (let i = 0; i < ORBS.length; i++) {
        const orb = ORBS[i];
        // Map document position to viewport position with parallax drift
        // When scrollY == orb.docY, the orb is centered vertically in viewport
        // drift slows down the movement so the orb "lingers"
        const viewportY = (orb.docY - scrollY) * (1 - orb.drift) + vh / 2;
        children[i].style.transform = `translate(-50%, -50%) translateY(${viewportY}px)`;
      }
      rafRef.current = null;
    };

    const onScroll = () => {
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    update(); // initial position

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isLight]);

  if (!isLight) return null;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
        overflow: 'hidden',
      }}
    >
      {ORBS.map((orb, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            left: orb.x,
            top: 0,
            width: orb.size,
            height: orb.size,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${orb.color} 0%, transparent 70%)`,
            willChange: 'transform',
          }}
        />
      ))}
    </Box>
  );
}
